/**
 * Get the date pattern used by Intl.DateTimeFormat for the given locale and options.
 *
 * @param locale - The locale to use (en-GB, en-US, fr-FR, de-DE, nl-NL, etc.)
 * @param options - The options to use (year: 'numeric', month: 'numeric', day: 'numeric', etc.)
 * @returns - The date patterns compatible with date-fns format
 */
export function getIntlDatePattern(locale: string, options: Intl.DateTimeFormatOptions): string {
    let localeToUse;
    try {
        localeToUse = new Intl.Locale(locale);
    } catch (e) {
        localeToUse = undefined;
    }
    const formatter = new Intl.DateTimeFormat(localeToUse, options);

    // Use a date with single-digit values to detect padding
    const date = new Date();
    date.setFullYear(2025);
    date.setMonth(1); // February
    date.setDate(5);
    date.setHours(9); // Single digit hour
    date.setMinutes(5); // Single digit minute
    date.setSeconds(5); // Single digit second

    const parts = formatter.formatToParts(date);

    return parts
        .map((part) => {
            switch (part.type) {
                case 'day':
                    return part.value.length === 2 ? 'dd' : 'd';
                case 'month':
                    return part.value.length === 2 ? 'MM' : 'M';
                case 'year':
                    return part.value.length === 4 ? 'yyyy' : 'yy';
                case 'hour':
                    // Check if 12-hour or 24-hour format based on options
                    if (options.hour12 === true || parts.some((p) => p.type === 'dayPeriod')) {
                        return part.value.length === 2 ? 'hh' : 'h';
                    } else {
                        return part.value.length === 2 ? 'HH' : 'H';
                    }
                case 'minute':
                    return part.value.length === 2 ? 'mm' : 'm';
                case 'second':
                    return part.value.length === 2 ? 'ss' : 's';
                case 'dayPeriod':
                    // AM/PM marker
                    return 'a';
                case 'literal':
                    return part.value;
                default:
                    return '';
            }
        })
        .join('');
}

/**
 * Get the date pattern used by Intl.DateTimeFormat for the given locale and options.
 *
 * @param locale - The locale to use (en-GB, en-US, fr-FR, de-DE, nl-NL, etc.)
 * @returns - The date pattern (dd/MM/yyyy, MM/dd/yyyy, dd.MM.yyyy, M/d/yyyy, d-M-yyyy, etc.)
 */
export function getIntlPatternFormatDateToShort(locale: string): string {
    return getIntlDatePattern(locale, {year: 'numeric', month: 'numeric', day: 'numeric'});
}

export function getIntlPatternFormatDateToShortWithHours(locale: string): string {
    return getIntlDatePattern(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
    });
}

export function getIntlPatternFormatTimeToShort(locale: string): string {
    return getIntlDatePattern(locale, {
        hour: 'numeric',
        minute: 'numeric',
    });
}
